<template>
  <v-card class="statistics-card">
    <!-- Main Content -->
    <v-container
      fluid
      class="pa-4 statistics-container"
    >
      <!-- Metrics Cards -->
      <v-row class="mb-4">
        <!-- Today Card -->
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            variant="outlined"
            class="metric-card"
          >
            <v-card-item class="pa-4">
              <div class="metric-header">
                <v-avatar
                  color="primary"
                  variant="flat"
                  size="48"
                  class="metric-icon"
                >
                  <span class="material-icons text-white">today</span>
                </v-avatar>
                <div class="metric-title">
                  <div class="text-h6 font-weight-bold text-primary">
                    Hoje
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    Atendimentos do dia
                  </div>
                </div>
              </div>
              
              <v-card-text class="pa-0 pt-3">
                <div
                  v-if="!loadMetricasDiarias"
                  class="metric-content"
                >
                  <div class="metric-value">
                    <p class="text-h2 text-primary font-weight-bold mb-2">
                      {{ totalAtendimentosDiario }}
                    </p>
                    <div class="loading-dots">
                      <div class="dot dot-primary" />
                      <div class="dot dot-secondary" />
                      <div class="dot dot-primary" />
                    </div>
                  </div>
                  <v-divider class="my-3" />
                  <div class="metric-detail">
                    <span class="material-icons text-secondary mr-2">schedule</span>
                    <div class="detail-text">
                      <p class="text-h5 text-secondary font-weight-bold mb-0">
                        {{ mediaTempoAtendimentoDiario }}
                      </p>
                      <span class="text-caption text-medium-emphasis">minutos médios</span>
                    </div>
                  </div>
                </div>
                <v-skeleton-loader 
                  v-if="loadMetricasDiarias"
                  type="image, article"
                />
              </v-card-text>
            </v-card-item>
          </v-card>
        </v-col>

        <!-- Monthly Card -->
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            variant="outlined"
            class="metric-card"
          >
            <v-card-item class="pa-4">
              <div class="metric-header">
                <v-avatar
                  color="secondary"
                  variant="flat"
                  size="48"
                  class="metric-icon"
                >
                  <span class="material-icons text-white">calendar_month</span>
                </v-avatar>
                <div class="metric-title">
                  <div class="text-h6 font-weight-bold text-secondary">
                    {{ currentMonth }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    Atendimentos mensais
                  </div>
                </div>
              </div>
              
              <v-card-text class="pa-0 pt-3">
                <div
                  v-if="!loadMetricasDiarias"
                  class="metric-content"
                >
                  <div class="metric-value">
                    <p class="text-h2 text-secondary font-weight-bold mb-2">
                      {{ totalAtendimentosMensal }}
                    </p>
                  </div>
                  <v-divider class="my-3" />
                  <div class="metric-detail">
                    <span class="material-icons text-primary mr-2">trending_up</span>
                    <div class="detail-text">
                      <p class="text-h5 text-primary font-weight-bold mb-0">
                        {{ mediaTempoAtendimentoMensal }}
                      </p>
                      <span class="text-caption text-medium-emphasis">minutos médios</span>
                    </div>
                  </div>
                </div>
                <v-skeleton-loader 
                  v-if="loadMetricasDiarias"
                  type="image, article"
                />
              </v-card-text>
            </v-card-item>
          </v-card>
        </v-col>

        <!-- Yearly Card -->
        <v-col
          cols="12"
          md="4"
        >
          <v-card
            variant="outlined"
            class="metric-card"
          >
            <v-card-item class="pa-4">
              <div class="metric-header">
                <v-avatar
                  color="primary"
                  variant="flat"
                  size="48"
                  class="metric-icon"
                >
                  <span class="material-icons text-white">auto_graph</span>
                </v-avatar>
                <div class="metric-title">
                  <div class="text-h6 font-weight-bold text-primary">
                    {{ currentYear }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    Atendimentos anuais
                  </div>
                </div>
              </div>
              
              <v-card-text class="pa-0 pt-3">
                <div
                  v-if="!loadMetricasDiarias"
                  class="metric-content"
                >
                  <div class="metric-value">
                    <p class="text-h2 text-primary font-weight-bold mb-2">
                      {{ totalAtendimentosAnual }}
                    </p>
                  </div>
                  <v-divider class="my-3" />
                  <div class="metric-detail">
                    <span class="material-icons text-secondary mr-2">insights</span>
                    <div class="detail-text">
                      <p class="text-h5 text-secondary font-weight-bold mb-0">
                        {{ mediaTempoAtendimentoAnual }}
                      </p>
                      <span class="text-caption text-medium-emphasis">minutos médios</span>
                    </div>
                  </div>
                </div>
                <v-skeleton-loader 
                  v-if="loadMetricasDiarias"
                  type="image, article"
                />
              </v-card-text>
            </v-card-item>
          </v-card>
        </v-col>
      </v-row>

      <!-- Charts Section -->
      <v-row>
        <!-- Status Chart -->
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            variant="outlined"
            class="chart-card"
          >
            <v-card-item class="pa-4">
              <div class="chart-header">
                <span class="material-icons text-primary mr-2">donut_large</span>
                <div class="text-h6 font-weight-bold text-primary">
                  Status das Senhas
                </div>
              </div>
            </v-card-item>
            <v-card-text class="chart-container">
              <apexchart 
                v-if="chartMounted"
                type="donut" 
                height="300" 
                :options="donutOptions" 
                :series="donutSeries"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Monthly Trend Chart -->
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            variant="outlined"
            class="chart-card"
          >
            <v-card-item class="pa-4">
              <div class="chart-header">
                <span class="material-icons text-secondary mr-2">show_chart</span>
                <div class="text-h6 font-weight-bold text-secondary">
                  Evolução Mensal
                </div>
              </div>
            </v-card-item>
            <v-card-text class="chart-container">
              <apexchart 
                v-if="chartMounted"
                type="area" 
                height="300" 
                :options="areaChartOptions" 
                :series="areaChartSeries"
              />
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts'
import medicoService from '../../services/medico.service'
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import { useTheme } from 'vuetify'

export default {
  name: 'MedicoEstatisticas',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const vuetifyTheme = useTheme()
    // 🔹 Estados reativos
    const user = ref(null)
    const load = ref(false)
    const loadMetricasDiarias = ref(false)

    const mediaTempoAtendimentoDiario = ref('0')
    const totalAtendimentosDiario = ref('0')
    const mediaTempoAtendimentoMensal = ref('0')
    const totalAtendimentosMensal = ref('0')
    const mediaTempoAtendimentoAnual = ref('0')
    const totalAtendimentosAnual = ref('0')

    const chartMounted = ref(false)

    const currentTheme = computed(() => (vuetifyTheme.global.current.value.dark ? 'dark' : 'light'))

     watch(() => props.isVisible, (newValue) => {
      if (newValue && !chartMounted.value) {
        setTimeout(() => {
          chartMounted.value = true;
        }, 100); 
      }
    });


    // 🔹 Configurações dos gráficos
    const donutOptions = ref({
      chart: {
        type: 'donut',
        toolbar: { show: false },
        background: 'transparent'
      },
      colors: ['#3f51b5', '#9c27b0', '#f44336', '#4caf50'],
      labels: ['Disponíveis', 'Em espera', 'Pendentes', 'Atendidos'],
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                fontSize: '16px',
                fontWeight: 600
              },
              value: {
                fontSize: '20px',
                fontWeight: 700
              }
            }
          }
        }
      },
      legend: {
        position: 'bottom'
      },
      tooltip: { theme: currentTheme.value }
    })

    const donutSeries = ref([15, 25, 35, 45])

    const areaChartOptions = ref({
      chart: {
        type: 'area',
        toolbar: { show: false },
        background: 'transparent'
      },
      colors: ['#3f51b5'],
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 3 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.2
        }
      },
      xaxis: {
        categories: [
          'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
          'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
        ]
      },
      grid: { borderColor: '#eee' },
      tooltip: { theme: currentTheme.value }
    })

    const areaChartSeries = ref([
      { name: 'Atendimentos', data: [120, 135, 145, 160, 155, 170, 185, 190, 200, 210, 220, 230] }
    ])

    // 🔹 Função: Buscar métricas mensais e anuais
    const retornarMetricasMensalAnual = async () => {
      load.value = true
      try {
        const response = await medicoService.medico.retornarMetricasDeAtendimentoMensaisAnuais(user.value.id)
        if (response.status === 200 && response.data?.metricas) {
          const { mensal, anual } = response.data.metricas

          if (mensal?.length) {
            const ultimoMes = mensal[mensal.length - 1]
            mediaTempoAtendimentoMensal.value = ultimoMes.mediaMinutos?.toFixed(1) || '0'
            totalAtendimentosMensal.value = ultimoMes.quantidade || '0'
          }

          if (anual?.length) {
            const ultimoAno = anual[anual.length - 1]
            mediaTempoAtendimentoAnual.value = ultimoAno.media?.toFixed(1) || '0'
            totalAtendimentosAnual.value = ultimoAno.quantidade || '0'
          }
        }
      } catch (error) {
        console.error('Erro ao buscar métricas mensais/anuais:', error)
      } finally {
        load.value = false
      }
    }

    // 🔹 Função: Buscar métricas diárias
    const retornarMetricasDiarias = async () => {
      loadMetricasDiarias.value = true
      try {
        const dataAtual = new Date()
        const dataFormatada = `${dataAtual.getFullYear()}-${String(dataAtual.getMonth() + 1).padStart(2, '0')}-${String(dataAtual.getDate()).padStart(2, '0')}`
        const response = await medicoService.medico.retornarMetricasDeAtendimento(user.value.id, dataFormatada, dataFormatada)

        if (response.status === 200) {
          const data = response.data
          mediaTempoAtendimentoDiario.value = data?.mediaMinutos?.toFixed(1) || '0'
          totalAtendimentosDiario.value = data?.quantidade || '0'
        }
      } catch (error) {
        console.error('Erro ao buscar métricas diárias:', error)
      } finally {
        loadMetricasDiarias.value = false
      }
    }

    // 🔹 Ciclo de vida
    onMounted(async () => {
      
      const storeUser = JSON.parse(localStorage.getItem('usuario'))
      user.value = storeUser || {}

      await retornarMetricasMensalAnual()
      await retornarMetricasDiarias()
    })

    onBeforeUnmount(() => {
      chartMounted.value = false
    })

    const currentMonth = new Date().toLocaleString('pt-BR', { month: 'long' })
    const currentYear = new Date().getFullYear()

    // 🔹 Retorno para o template
    return {
      user,
      load,
      loadMetricasDiarias,
      mediaTempoAtendimentoDiario,
      totalAtendimentosDiario,
      mediaTempoAtendimentoMensal,
      totalAtendimentosMensal,
      mediaTempoAtendimentoAnual,
      totalAtendimentosAnual,
      donutOptions,
      donutSeries,
      areaChartOptions,
      areaChartSeries,
      currentMonth,
      currentYear,
      chartMounted,
      currentTheme
    }
  }
}
</script>


<style scoped>
.statistics-card {
  height: 100vh;
  width: 100%;
  background: rgb(var(--v-theme-background));
}

.statistics-container {
  height: calc(100vh - 64px);
  overflow-y: auto;
}

.metric-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  transition: all 0.3s ease;
  height: 100%;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.metric-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.metric-icon {
  margin-right: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.metric-title {
  flex: 1;
}

.metric-content {
  display: flex;
  flex-direction: column;
}

.metric-value {
  text-align: center;
}

.metric-detail {
  display: flex;
  align-items: center;
  gap: 12px;
}

.detail-text {
  flex: 1;
}

.chart-card {
  background: rgb(var(--v-theme-surface));
  border-radius: 12px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  transition: all 0.3s ease;
  height: 100%;
}

.chart-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.chart-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.chart-container {
  padding: 0;
}

.loading-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20px;
  gap: 4px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: bounce 0.8s alternate infinite;
}

.dot-primary {
  background-color: rgb(var(--v-theme-primary));
}

.dot-secondary {
  background-color: rgb(var(--v-theme-secondary));
}

.dot:nth-child(1) {
  animation-delay: 0s;
}

.dot:nth-child(2) {
  animation-delay: 0.2s;
}

.dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-6px) scale(1.1);
  }
  100% {
    transform: translateY(0) scale(1);
  }
}

/* Responsividade */
@media (max-width: 960px) {
  .metric-card {
    margin-bottom: 16px;
  }
  
  .chart-card {
    margin-bottom: 16px;
  }
}

@media (max-width: 600px) {
  .text-h2 {
    font-size: 2.5rem !important;
  }
  
  .text-h5 {
    font-size: 1.25rem !important;
  }
  
  .metric-detail {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
  
  .chart-header {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }
}

/* Scrollbar personalizada */
.statistics-container::-webkit-scrollbar {
  width: 6px;
}

.statistics-container::-webkit-scrollbar-track {
  background: rgba(var(--v-theme-on-surface), 0.1);
  border-radius: 3px;
}

.statistics-container::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-primary), 0.3);
  border-radius: 3px;
}

.statistics-container::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--v-theme-primary), 0.5);
}
</style>